/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable no-negated-condition */
/* eslint-disable max-statements */
import { AddAutomationData } from '@params/redux/automation';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Edge, Node } from '@xyflow/react';

// Define initial state
interface WorkflowState {
    nodes: Node[];
    edges: Edge[];
    selectedEdge: Edge | null;
    selectedNode: Node | null;
}

const initialState: WorkflowState = {
    nodes: [{ id: '1', type: 'action', data: { label: 'Add Action' }, position: { x: 500, y: 0 }}],
    edges: [],
    selectedEdge: null,
    selectedNode: null
};

const getMaxNodeInConditionPath = (nodes: Node[], parentId: string) => {
    return nodes
        .filter((node) => node.data?.parentNodeId === parentId)
        .map((node) => {
            const parts = node.id.split('.');
            return parseInt(parts[2], 10) || 0;
        })
        .filter((id) => !isNaN(id))
        .reduce((max, id) => Math.max(max, id), 0);
};

const getMaxNodeId = (nodes: Node[]) => {
    return nodes
        .map((node) => parseFloat(node.id))
        .filter((id) => !isNaN(id))
        .reduce((max, id) => Math.max(max, id), 0);
};

const updateEdges = (nodes: Node[]): Edge[] => {
    const edges: Edge[] = [];
    const visitedNodes = new Set<string>();

    nodes.forEach((node, index) => {
        if (visitedNodes.has(node.id)) return;

        // 🔹 Handle Normal Node Flow (Sequential)
        if (index < nodes.length - 1 && nodes[index + 1]?.type !== 'addCondition' && node?.type !== 'endCondition') {
            const edgeId = `e-${node.id}-${nodes[index + 1].id}`;
            if (!visitedNodes.has(edgeId)) {
                edges.push({
                    id: edgeId,
                    source: node.id,
                    target: nodes[index + 1].id,
                    type: 'custom',
                    data: {
                        id: edgeId,
                        previousNodeId: node.id,
                        nextNodeId: nodes[index + 1].id,
                        label: 'PLUS'
                    }
                });
                visitedNodes.add(edgeId);
            }
        }

        // 🔹 Handle Condition Nodes (Extract Flow Until endCondition)
        if (node.type === 'condition') {
            const conditionBlock = nodes.filter((n) => n.data.parentNodeId === node.id);

            // Separate "true" and "false" paths
            const truePath = conditionBlock.filter((n) => n.data.conditionType === true);
            const falsePath = conditionBlock.filter((n) => n.data.conditionType === false);

            // Connect parent condition to the first node of each path
            if (truePath.length > 0) {
                const edgeId = `e-${node.id}-${truePath[0].id}`;
                if (!visitedNodes.has(edgeId)) {
                    edges.push({
                        id: edgeId,
                        source: node.id,
                        target: truePath[0].id,
                        type: 'simpleEdge',
                        data: {
                            id: edgeId,
                            previousNodeId: node.id,
                            nextNodeId: truePath[0].id,
                            label: 'is true'
                        }
                    });
                    visitedNodes.add(edgeId);
                }
            }
            if (falsePath.length > 0) {
                const edgeId = `e-${node.id}-${falsePath[0].id}`;
                if (!visitedNodes.has(edgeId)) {
                    edges.push({
                        id: edgeId,
                        source: node.id,
                        target: falsePath[0].id,
                        type: 'simpleEdge',
                        data: {
                            id: edgeId,
                            previousNodeId: node.id,
                            nextNodeId: falsePath[0].id,
                            label: 'is false'
                        }
                    });
                    visitedNodes.add(edgeId);
                }
            }

            // Connect nodes sequentially in each path
            const connectSequentialNodes = (path: Node[], label: string) => {
                for (let i = 0; i < path.length - 1; i++) {
                    const edgeId = `e-${path[i].id}-${path[i + 1].id}`;
                    if (!visitedNodes.has(edgeId)) {
                        edges.push({
                            id: edgeId,
                            source: path[i].id,
                            target: path[i + 1].id,
                            type: 'custom',
                            data: { label, condition: label.includes('True') }
                        });
                        visitedNodes.add(edgeId);
                    }
                }
            };

            connectSequentialNodes(truePath, 'True Path');
            connectSequentialNodes(falsePath, 'False Path');

            const endCondition = nodes.find((n) => n.type === 'endCondition' && n.data.parentNodeId === node.id);
            // Connect last node of both paths to endCondition
            if (endCondition) {
                if (truePath.length > 0) {
                    const edgeId = `e-${truePath[truePath.length - 1].id}-${endCondition.id}`;
                    if (!visitedNodes.has(edgeId)) {
                        edges.push({
                            id: edgeId,
                            source: truePath[truePath.length - 1].id,
                            target: endCondition.id,
                            type: 'custom',
                            data: {
                                id: edgeId,
                                previousNodeId: truePath[truePath.length - 1].id,
                                nextNodeId: endCondition.id,
                                label: 'End True Path',
                                condition: true
                            }
                        });
                        visitedNodes.add(edgeId);
                    }
                }
                if (falsePath.length > 0) {
                    const edgeId = `e-${falsePath[falsePath.length - 1].id}-${endCondition.id}`;
                    if (!visitedNodes.has(edgeId)) {
                        edges.push({
                            id: edgeId,
                            source: falsePath[falsePath.length - 1].id,
                            target: endCondition.id,
                            type: 'custom',
                            data: {
                                id: edgeId,
                                previousNodeId: falsePath[falsePath.length - 1].id,
                                nextNodeId: endCondition.id,
                                label: 'End False Path',
                                condition: false
                            }
                        });
                        visitedNodes.add(edgeId);
                    }
                }
            }
            const endConditionIndex = nodes.findIndex((n) => n.id === endCondition?.id);
            // If there is a node after endCondition, connect it
            if (endCondition && endConditionIndex + 1 < nodes.length) {
                edges.push({
                    id: `e-${endCondition.id}-${nodes[endConditionIndex + 1].id}`,
                    source: endCondition.id,
                    target: nodes[endConditionIndex + 1].id,
                    type: 'simpleEdge',
                    data: {
                        id: `e-${endCondition.id}-${nodes[endConditionIndex + 1].id}`,
                        previousNodeId: endCondition.id,
                        nextNodeId: nodes[endConditionIndex + 1].id,
                        label: 'AFTER END'
                    }
                });
            }
            visitedNodes.add(node.id);
        }
    });

    return edges;
};

const handleUpdateAllThePositionsY = (nodes: Node[]): Node[] => {
    if (nodes.length === 0) return nodes;

    // Deep copy nodes to avoid mutation issues
    const updatedNodes = [...nodes.map((node) => ({ ...node, position: { ...node.position }}))];

    let currentY = 100;
    const yOffset = 100;
    const visitedNodes = new Set<string>();

    for (let i = 0; i < updatedNodes.length; i++) {
        const node = updatedNodes[i];

        // eslint-disable-next-line no-continue
        if (visitedNodes.has(node.id)) continue;
        visitedNodes.add(node.id);

        // Set first node's position
        node.position.y = currentY;
        currentY += yOffset;

        // If it's a condition node, handle its paths
        if (node.type === 'condition') {
            const truePath = updatedNodes.filter((n) => n.data.parentNodeId === node.id && n.id.includes(`${node.id}.1.`));
            const falsePath = updatedNodes.filter((n) => n.data.parentNodeId === node.id && n.id.includes(`${node.id}.2.`));
            const endCondition = updatedNodes.find((n) => n.type === 'endCondition' && n.data.parentNodeId === node.id);

            const trueY = node.position.y + yOffset;
            const falseY = node.position.y + yOffset;

            // Move First Nodes of True/False Paths from Condition Node
            if (truePath.length > 0) {
                truePath[0].position.y = trueY;
                visitedNodes.add(truePath[0].id);
            }
            if (falsePath.length > 0) {
                falsePath[0].position.y = falseY;
                visitedNodes.add(falsePath[0].id);
            }

            // Move Remaining True Path Nodes based on Previous Node in True Path
            for (let j = 1; j < truePath.length; j++) {
                truePath[j].position.y = truePath[j - 1].position.y + yOffset;
                visitedNodes.add(truePath[j].id);
            }

            // Move Remaining False Path Nodes based on Previous Node in False Path
            for (let j = 1; j < falsePath.length; j++) {
                falsePath[j].position.y = falsePath[j - 1].position.y + yOffset;
                visitedNodes.add(falsePath[j].id);
            }

            // Place `endCondition` Below the Longest Path
            const longestPathY = Math.max(
                truePath.length > 0 ? truePath[truePath.length - 1].position.y + yOffset : trueY,
                falsePath.length > 0 ? falsePath[falsePath.length - 1].position.y + yOffset : falseY
            );

            if (endCondition) {
                endCondition.position.y = longestPathY + yOffset;
                currentY = longestPathY + 200;
                visitedNodes.add(endCondition.id);
            }
        }
    }

    return updatedNodes;
};

export const workflowSlice = createSlice({
    name: 'workflow',
    initialState,
    reducers: {
        settingSelectedEdge: (state, action: PayloadAction<Edge | null>) => {
            state.selectedEdge = action.payload;
        },

        settingNodes: (state, action: PayloadAction<Node[]>) => {
            if (action.payload) {
                const updatedNodes = action.payload.filter((node: Node) => node.type !== 'action');
                const lastNode = updatedNodes[updatedNodes.length - 1];
                const actionNode = {
                    id: '1',
                    type: 'action',
                    data: { label: 'Add Action' },
                    position: {
                        x: 500,
                        y: (lastNode?.position.y || 0) + 100
                    }
                };
                state.nodes = [...updatedNodes, actionNode];
                state.edges = updateEdges(action.payload);
            }
        },

        settingSelectedNode: (state, action: PayloadAction<Node | null>) => {
            state.selectedNode = action.payload;
        },

        editSelectedNode: (state, action: PayloadAction<AddAutomationData>) => {
            const selectedNodeIndex = state.nodes.findIndex((node) => node.id === state.selectedNode?.id);
            state.nodes[selectedNodeIndex].data = {
                ...state.nodes[selectedNodeIndex].data,
                ...action.payload,
                label: action.payload.type === 'timer' ? `${action.payload.duration} ${action.payload.duration_type}` : action.payload.action_name
            };
            state.selectedNode = null;
        },

        addNode: (state, action: PayloadAction<AddAutomationData>) => {
            const nodesToSend = [...state.nodes.slice(0, -1)];
            if (state.selectedEdge?.id.includes('.')) {
                const previousNode = state.nodes.find((node) => node.id === state.selectedEdge?.source);
                const previousNodeIndex = state.nodes.findIndex((node) => node.id === state.selectedEdge?.source);
                const nextNode = state.nodes.find((node) => node.id === state.selectedEdge?.target);
                const parentNode = state.nodes.find((node) => node.id === previousNode?.data.parentNodeId) || null;

                const maxInPath = getMaxNodeInConditionPath(state.nodes, parentNode?.id || '0');

                const newNodeId = `${parentNode?.id}.${previousNode?.id.split('.')[1]}.${maxInPath + 1}`;
                const newNode: Node = {
                    id: newNodeId,
                    type: action.payload.type === 'email' ? 'emailConditionItem' : action.payload.type === 'phone' ? 'phoneConditionItem' : 'timerConditionItem',
                    data: {
                        ...action.payload,
                        label: action.payload.type === 'timer' ? `${action.payload.duration} ${action.payload.duration_type}` : action.payload.action_name,
                        parentNodeId: parentNode?.id,
                        previousNodeId: previousNode?.id,
                        nextNodeId: nextNode?.id,
                        conditionFirst: false,
                        conditionType: previousNode?.id.split('.')[1] === '1',
                        id: newNodeId
                    },
                    position: { x: previousNode?.id.split('.')[1] === '1' ? 90 : 945, y: (previousNode?.position?.y || 600) + 100 }
                };

                if (nextNode) {
                    nextNode.data.previousNodeId = newNodeId;
                }
                if (previousNode) {
                    previousNode.data.nextNodeId = newNodeId;
                }

                state.nodes.splice(previousNodeIndex + 1, 0, newNode);
                nodesToSend.splice(previousNodeIndex + 1, 0, newNode);
            } else {
                const maxNodeId = getMaxNodeId(state.nodes);
                const newNodeId = `${maxNodeId + 1}`;
                const isConditionNode = action.payload.type === 'condition';

                // Add Node in between 2 nodes
                if (state.selectedEdge) {
                    const sourceNode = state.nodes.find((node) => node.id === state.selectedEdge?.source);
                    const sourceNodeIndex = state.nodes.findIndex((node) => node.id === state.selectedEdge?.source);
                    const targetNode = state.nodes.find((node) => node.id === state.selectedEdge?.target);
                    // create node email, phone, timer and condition
                    const newNode: Node = {
                        id: newNodeId,
                        type: action.payload.type,
                        data: {
                            ...action.payload,
                            label: action.payload.type === 'timer' ? `${action.payload.duration} ${action.payload.duration_type}` : action.payload.action_name,
                            previousNodeId: state.selectedEdge?.source,
                            nextNodeId: isConditionNode ? `${maxNodeId + 2}` : state.selectedEdge?.target,
                            id: newNodeId
                        },
                        position: { x: 500, y: targetNode?.position.y || 250 }
                    };

                    if (sourceNode) sourceNode.data.nextNodeId = newNodeId;
                    if (targetNode) targetNode.data.previousNodeId = isConditionNode ? `${maxNodeId + 2}` : newNodeId;

                    state.nodes.splice(sourceNodeIndex + 1, 0, newNode);
                    nodesToSend.splice(sourceNodeIndex + 1, 0, newNode);
                    // Create Condition's Child Node True, False and end
                    if (isConditionNode) {
                        const trueNodeId = `${newNodeId}.1.1`;
                        const falseNodeId = `${newNodeId}.2.1`;
                        const endNodeId = `${maxNodeId + 2}`;

                        const trueNode: Node = {
                            id: trueNodeId,
                            type: 'addCondition',
                            data: { label: 'True Path', id: trueNodeId, parentNodeId: newNodeId, previousNodeId: newNodeId, nextNodeId: endNodeId, conditionType: true, conditionFirst: true },
                            position: { x: 250, y: (state.nodes.length * 100) + 100 }
                        };

                        const falseNode: Node = {
                            id: falseNodeId,
                            type: 'addCondition',
                            data: { label: 'False Path', id: falseNodeId, parentNodeId: newNodeId, previousNodeId: newNodeId, nextNodeId: endNodeId, conditionType: false, conditionFirst: true },
                            position: { x: 1100, y: (state.nodes.length * 100) + 100 }
                        };
                        const endNode: Node = {
                            id: endNodeId,
                            type: 'endCondition',
                            data: { label: 'End Condition', id: endNodeId, parentNodeId: newNodeId, previousNodeId: newNodeId, nextNodeId: targetNode?.id },
                            position: { x: 690, y: (state.nodes.length * 100) + 250 }
                        };

                        nodesToSend.splice(sourceNodeIndex + 2, 0, trueNode, falseNode, endNode);
                        state.nodes.splice(sourceNodeIndex + 2, 0, trueNode, falseNode, endNode);
                    }
                } else {
                    // Create New Node at end
                    // Find the last non-ActionNode
                    const lastNode: Node | null | undefined = state.nodes.find((node) => node.id === (maxNodeId).toString());
                    const lastNodeIndex = state.nodes.length - 1;

                    // Create new node
                    const newNode: Node = {
                        id: newNodeId,
                        type: action.payload.type,
                        data: {
                            ...action.payload,
                            label: action.payload.type === 'timer' ? `${action.payload.duration} ${action.payload.duration_type}` : action.payload.action_name,
                            previousNodeId: lastNode?.id || null,
                            nextNodeId: null,
                            id: newNodeId
                        },
                        position: { x: 500, y: state.nodes.length * 100 }
                    };
                    if (lastNode) {
                        lastNode.data.nextNodeId = newNodeId;
                    }

                    // Insert new node before ActionNode
                    state.nodes.splice(lastNodeIndex, 0, newNode);
                    nodesToSend.splice(lastNodeIndex, 0, newNode);

                    // 📌 Handle Edge Creation for Regular Nodes
                    if (isConditionNode) {
                        const trueNodeId = `${newNodeId}.1.1`;
                        const falseNodeId = `${newNodeId}.2.1`;
                        const trueNode: Node = {
                            id: trueNodeId,
                            type: 'addCondition',
                            data: {
                                label: 'True Path',
                                id: trueNodeId,
                                parentNodeId: newNode.id,
                                previousNodeId: newNode.id,
                                nextNodeId: null,
                                conditionType: true,
                                conditionFirst: true
                            },
                            position: { x: 250, y: (state.nodes.length * 100) + 100 }
                        };

                        const falseNode: Node = {
                            id: falseNodeId,
                            type: 'addCondition',
                            data: {
                                label: 'False Path',
                                id: falseNodeId,
                                parentNodeId: newNode.id,
                                previousNodeId: newNode.id,
                                nextNodeId: null,
                                conditionType: false,
                                conditionFirst: true
                            },
                            position: { x: 1100, y: (state.nodes.length * 100) + 100 }
                        };

                        const endNode: Node = {
                            id: (parseInt(newNodeId, 10) + 1).toString(),
                            type: 'endCondition',
                            data: {
                                label: 'True Path',
                                id: (parseInt(newNodeId, 10) + 1).toString(),
                                parentNodeId: newNode.id,
                                previousNodeId: newNode.id,
                                nextNodeId: null
                            },
                            position: { x: 690, y: (state.nodes.length * 100) + 250 }
                        };
                        state.nodes.splice(lastNodeIndex + 2, 0, trueNode, falseNode, endNode);
                        nodesToSend.splice(lastNodeIndex + 2, 0, trueNode, falseNode, endNode);
                    }
                    // 📌 Ensure ActionNode Stays at the Bottom and Connects to Last Node
                }
                const actionNode = state.nodes.find((node) => node.type === 'action');
                if (actionNode) {
                    actionNode.position.y = !isConditionNode ? state.nodes.length * 100 : state.nodes.length * 110;
                    state.nodes.push(state.nodes.splice(state.nodes.indexOf(actionNode), 1)[0]);
                }
            }
            const updatedNodes = handleUpdateAllThePositionsY(state.nodes);
            state.nodes = updatedNodes;
            state.edges = updateEdges(nodesToSend);
        },

        // 📌 Delete a Node
        deleteNode: (state, action: PayloadAction<string>) => {
            const nodeId = action.payload;
            const nodeToDeleteIndex = state.nodes.findIndex((node) => node.id === nodeId);
            if (nodeToDeleteIndex === -1) return;
            const nodeToDelete = state.nodes[nodeToDeleteIndex];
            if (nodeId.includes('.')) {
                const previousNode = state.nodes.find((node) => node.id === nodeToDelete?.data.previousNodeId);
                const nextNode = state.nodes.find((node) => node.id === nodeToDelete?.data.nextNodeId);
                const parentNode = state.nodes.find((node) => node.id === nodeToDelete?.data.parentNodeId) || null;
                // ✅ Find `endCondition` for this condition block
                const endCondition = state.nodes.find((node) => node.type === 'endCondition' && node.data.parentNodeId === parentNode?.id);

                // ✅ Find all `trueNodes` (6.1.x) and `falseNodes` (6.2.x)
                const trueNodes = state.nodes.filter((node) => node.data.parentNodeId === parentNode?.id && node.id.includes('.1.'));
                const falseNodes = state.nodes.filter((node) => node.data.parentNodeId === parentNode?.id && node.id.includes('.2.'));
                const isAddingToTrueNodes = nodeId.includes('.1.');
                const isAddingToFalseNodes = nodeId.includes('.2.');
                const trueNodesLengthBefore = trueNodes.length;
                const falseNodesLengthBefore = falseNodes.length;

                let shouldUpdateEndConditionY = false;

                if (isAddingToTrueNodes && trueNodesLengthBefore < falseNodesLengthBefore) {
                    // 📌 If adding to True Path and True Path is already longer → Move `endCondition.y`
                    shouldUpdateEndConditionY = true;
                } else if (isAddingToFalseNodes && falseNodesLengthBefore < trueNodesLengthBefore) {
                    // 📌 If adding to False Path and False Path is already longer → Move `endCondition.y`
                    shouldUpdateEndConditionY = true;
                }

                if (shouldUpdateEndConditionY && endCondition) {
                    endCondition.position.y -= 100;
                    const endConditionIndex = state.nodes.findIndex((node) => node.id === endCondition.id);
                    for (let i = endConditionIndex + 1; i < state.nodes.length; i++) {
                        state.nodes[i].position.y -= 100;
                    }
                }

                // ✅ Update previous node's `nextNodeId`
                if (previousNode) {
                    previousNode.data.nextNodeId = nextNode ? nextNode.id : null;
                }

                // ✅ Update next node's `previousNodeId` and position
                if (nextNode) {
                    nextNode.data.previousNodeId = previousNode ? previousNode.id : null;
                    nextNode.position.y = nodeToDelete?.position.y || 600;
                }

                if (nextNode && nextNode.id !== endCondition?.id) {
                    const nextNodeIndex = state.nodes.findIndex((node) => node.id === nextNode.id);
                    // ✅ Check if new node is being added to `.1.` (true) or `.2.` (false) path
                    const isAddingToTruePath = nodeId.includes('.1.');
                    const isAddingToFalsePath = nodeId.includes('.2.');

                    for (let i = nextNodeIndex; i < state.nodes.length; i++) {
                        // ✅ Only update `.1.` nodes when adding to `.1.`
                        if (isAddingToTruePath && state.nodes[i].id.includes('.1.')) {
                            state.nodes[i].position.y -= 100;
                        }
                        // ✅ Only update `.2.` nodes when adding to `.2.`
                        if (isAddingToFalsePath && state.nodes[i].id.includes('.2.')) {
                            state.nodes[i].position.y -= 100;
                        }
                    }
                }

                // ✅ Remove the node
                state.nodes = state.nodes.filter((node) => node.id !== nodeId);
            } else if (nodeToDelete.type === 'condition') {
                const nodesToRemove: string[] = [nodeId];

                // Find the endCondition node
                let endConditionIndex = -1;
                for (let i = nodeToDeleteIndex + 1; i < state.nodes.length; i++) {
                    const currentNode = state.nodes[i];
                    if (currentNode.type === 'endCondition') {
                        endConditionIndex = i;
                        nodesToRemove.push(currentNode.id);
                        break;
                    }
                    nodesToRemove.push(currentNode.id);
                }

                if (endConditionIndex === -1) {
                    console.warn('No endCondition found for condition node', nodeId);
                    return;
                }

                // 🔹 Update Position and References for the Node After `endCondition`
                const nextNodeIndex = endConditionIndex + 1;
                if (nextNodeIndex < state.nodes.length) {
                    const nextNode = state.nodes[nextNodeIndex];
                    const previousNode = state.nodes.find((node) => node.id === nodeToDelete.data.previousNodeId);

                    // ✅ Update previousNodeId of nextNode to skip the removed condition block
                    if (previousNode) {
                        nextNode.data.previousNodeId = previousNode.id;
                    } else {
                        nextNode.data.previousNodeId = null;
                    }

                    // ✅ Adjust position of nextNode to maintain flow
                    nextNode.position = nodeToDelete.position;
                }

                // 🔹 Update the previous node's `nextNodeId` (DO NOT change position)
                const previousNode = state.nodes.find((node) => node.id === nodeToDelete.data.previousNodeId);
                if (previousNode) {
                    previousNode.data.nextNodeId = state.nodes[nextNodeIndex]?.id || null;
                }

                // ✅ Remove condition block nodes
                state.nodes = state.nodes.filter((node) => !nodesToRemove.includes(node.id));
                // 🟢 If Deleting a Regular Node
            } else {
                const previousNode = state.nodes.find((node) => node.id === nodeToDelete.data.previousNodeId);
                const nextNode = state.nodes.find((node) => node.id === nodeToDelete.data.nextNodeId);

                if (nextNode) {
                    nextNode.data.previousNodeId = nodeToDelete.data.previousNodeId;
                    nextNode.position = nodeToDelete.position;
                }
                if (previousNode) {
                    previousNode.data.nextNodeId = nodeToDelete.data.nextNodeId;
                }

                state.nodes.splice(nodeToDeleteIndex, 1);
            }

            const updatedNodes = handleUpdateAllThePositionsY(state.nodes);
            state.nodes = updatedNodes;
            state.edges = updateEdges(state.nodes.slice(0, -1));
            // ✅ Update edges after deletion
        },

        // 📌 Duplicate a Node
        duplicateNode: (state, action: PayloadAction<string>) => {
            const nodeId = action.payload;
            const nodeToDuplicate = state.nodes.find((node) => node.id === nodeId);
            if (!nodeToDuplicate) return;
            const duplicateIndex = state.nodes.findIndex((node) => node.id === nodeId);
            if (duplicateIndex === -1) return;
            if (nodeId.includes('.')) {
                const nextNode = state.nodes.find((node) => node.id === nodeToDuplicate.data?.nextNodeId);
                const parentNode = state.nodes.find((node) => node.id === nodeToDuplicate?.data.parentNodeId) || null;
                const maxInPath = getMaxNodeInConditionPath(state.nodes, parentNode?.id || '0');
                const newNodeId = `${parentNode?.id}.${nodeToDuplicate?.id.split('.')[1]}.${maxInPath + 1}`;
                const newNode: Node = {
                    id: newNodeId,
                    type: nodeToDuplicate.type,
                    data: {
                        ...nodeToDuplicate.data,
                        label: `${nodeToDuplicate.data.label} Copy`,
                        previousNodeId: nodeToDuplicate?.id,
                        conditionFirst: false,
                        id: newNodeId
                    },
                    position: { x: nodeToDuplicate?.id.split('.')[1] === '1' ? 90 : 945, y: (nodeToDuplicate?.position?.y || 600) + 100 }
                };

                // ✅ Find `endCondition` for this condition block
                const endCondition = state.nodes.find((node) => node.type === 'endCondition' && node.data.parentNodeId === parentNode?.id);

                // ✅ Find all `trueNodes` (6.1.x) and `falseNodes` (6.2.x)
                const trueNodes = state.nodes.filter((node) => node.data.parentNodeId === parentNode?.id && node.id.includes('.1.'));
                const falseNodes = state.nodes.filter((node) => node.data.parentNodeId === parentNode?.id && node.id.includes('.2.'));

                // ✅ Compare lengths before adding new node
                const isAddingToTrueNodes = newNodeId.includes('.1.');
                const isAddingToFalseNodes = newNodeId.includes('.2.');
                const trueNodesLengthBefore = trueNodes.length;
                const falseNodesLengthBefore = falseNodes.length;

                // ✅ Determine whether to increase Y position of `endCondition`
                let shouldUpdateEndConditionY = false;

                if (trueNodesLengthBefore === falseNodesLengthBefore) {
                    // 📌 If true and false paths are equal before adding → Move `endCondition.y`
                    shouldUpdateEndConditionY = true;
                } else if (isAddingToTrueNodes && trueNodesLengthBefore > falseNodesLengthBefore) {
                    // 📌 If adding to True Path and True Path is already longer → Move `endCondition.y`
                    shouldUpdateEndConditionY = true;
                } else if (isAddingToFalseNodes && falseNodesLengthBefore > trueNodesLengthBefore) {
                    // 📌 If adding to False Path and False Path is already longer → Move `endCondition.y`
                    shouldUpdateEndConditionY = true;
                }


                if (nextNode && nextNode.id !== endCondition?.id) {
                    const nextNodeIndex = state.nodes.findIndex((node) => node.id === nextNode.id);

                    for (let i = nextNodeIndex; i < state.nodes.length; i++) {
                        // ✅ Only update `.1.` nodes when adding to `.1.`
                        if (isAddingToTrueNodes && state.nodes[i].id.includes('.1.')) {
                            state.nodes[i].position.y += 100;
                        }
                        // ✅ Only update `.2.` nodes when adding to `.2.`
                        if (isAddingToFalseNodes && state.nodes[i].id.includes('.2.')) {
                            state.nodes[i].position.y += 100;
                        }
                    }
                }

                if (shouldUpdateEndConditionY && endCondition) {
                    endCondition.position.y += 100;
                    const endConditionIndex = state.nodes.findIndex((node) => node.id === endCondition.id);
                    for (let i = endConditionIndex + 1; i < state.nodes.length; i++) {
                        if (!state.nodes[i].id.includes('.')) {
                            state.nodes[i].position.y += 100;
                        }
                    }
                }

                if (nextNode) {
                    nextNode.data.previousNodeId = newNodeId;
                }
                nodeToDuplicate.data.nextNodeId = newNodeId;

                state.nodes.splice(duplicateIndex + 1, 0, newNode);
                //
            } else {
                // 🟢 Skip duplication for condition, conditionItem, and endCondition nodes
                if (['condition', 'addCondition', 'endCondition'].includes(nodeToDuplicate?.type || '')) {
                    console.warn(`Skipping duplication for ${nodeToDuplicate.type} node.`);
                    return;
                }
                const maxNodeId = getMaxNodeId(state.nodes);
                const newNodeId = `${maxNodeId + 1}`;

                // 🟢 Find the next node in the sequence
                const nextNode = state.nodes[duplicateIndex + 1];

                // 🟢 Create the new node with updated references
                const newNode: Node = {
                    ...nodeToDuplicate,
                    id: newNodeId,
                    data: {
                        ...nodeToDuplicate.data,
                        id: newNodeId,
                        previousNodeId: nodeId,
                        nextNodeId: nextNode ? nextNode.id : null
                    },
                    position: { x: nodeToDuplicate.position.x, y: nodeToDuplicate.position.y + 100 }
                };

                // 🟢 Update `nextNodeId` of selected node to point to new node
                state.nodes[duplicateIndex].data.nextNodeId = newNodeId;

                // 🟢 If a next node exists, update its `previousNodeId`
                if (nextNode) {
                    nextNode.data.previousNodeId = newNodeId;
                }

                // 🟢 Insert the new node after the selected one
                state.nodes.splice(duplicateIndex + 1, 0, newNode);
                // 🟢 Move all subsequent nodes down in Y-position
                for (let i = duplicateIndex + 2; i < state.nodes.length; i++) {
                    state.nodes[i].position.y += 100;
                }
            }
            const updatedNodes = handleUpdateAllThePositionsY(state.nodes);
            state.nodes = updatedNodes;
            // 🟢 Update edges
            state.edges = updateEdges(state.nodes);
        },

        resettingWorkFlowData: () => initialState
    }
});

export const { settingSelectedEdge, addNode, deleteNode, duplicateNode, settingNodes, settingSelectedNode, editSelectedNode, resettingWorkFlowData } = workflowSlice.actions;
export default workflowSlice.reducer;
